import * as React from 'react';
import styled from 'styled-components';

import { ActionMenu } from 'ui/actionMenu';
import { SecondaryButton } from 'ui/button';
import { colors, spacing } from 'ui/theme';
import { Delete, Overflow, MoveToFolder, EditFolder, FolderClosed } from 'ui/icons';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';

import { FolderId, FolderIdData } from 'modules/domains/foldering';

const { useMemo } = React;

const displayText = {
  moveOption: 'Move to folder',
  deleteOption: 'Delete folder',
  renameOption: 'Rename folder',
  newFolder: 'New subfolder',
};

const StyledButton = styled(SecondaryButton)`
  padding: 0 ${spacing.half};
`;

const StyledFolderClosed = styled(FolderClosed)`
  && {
    path {
      stroke: none;
    }
  }
`;

type WorkflowFolderRowOverflowMenuProps = {
  folder: FolderIdData;
  onDeleteOptionClick: (folderId: FolderId) => void;
  onMoveOptionClick: (folderId: FolderId) => void;
  onRenameOptionClick: (folderId: FolderId) => void;
  onCreateOptionClick: (folderId: FolderId) => void;
};

export const WorkflowFolderRowOverflowMenu = ({
  folder,
  onDeleteOptionClick,
  onMoveOptionClick,
  onRenameOptionClick,
  onCreateOptionClick,
}: WorkflowFolderRowOverflowMenuProps) => {
  const options = useMemo(
    (): ActionMenuOption[] => [
      {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <StyledFolderClosed height={16} />
            <span>{displayText.newFolder}</span>
          </>
        ),
        id: 'new-folder',
        onClick: () => onCreateOptionClick(folder.id),
      },
      {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <MoveToFolder height={16} />
            <span>{displayText.moveOption}</span>
          </>
        ),
        id: 'move-to-folder',
        onClick: () => onMoveOptionClick(folder.id),
      },
      {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <EditFolder height={16} />
            <span>{displayText.renameOption}</span>
          </>
        ),
        id: 'rename-folder',
        onClick: () => onRenameOptionClick(folder.id),
      },
      {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <Delete height={16} />
            <span>{displayText.deleteOption}</span>
          </>
        ),
        id: 'delete',
        onClick: () => onDeleteOptionClick(folder.id),
      },
    ],
    [onMoveOptionClick, onDeleteOptionClick, folder.id],
  );

  return (
    <ActionMenu
      align={MENU_ALIGNMENTS.RIGHT}
      options={options}
      id={`workflow-folder-overflow-menu-${folder.id}`}
    >
      <StyledButton onClick={() => null}>
        <Overflow color={colors.grey90} />
      </StyledButton>
    </ActionMenu>
  );
};
